import type { ReactNode } from "react";

import { cx } from "@tripcart/traveler-ui/cx";

import styles from "./merch-flag.module.css";

/**
 * The style guide's `.flag` — a merchandising claim about the PRODUCT.
 *
 * Deliberately not a `<Badge>`, because it is not a badge. The guide draws the
 * two differently and means different things by them: a badge is 22px / 11.5px
 * / 600 and marks a state or a label, while a flag is 24px / 11px / 700 in
 * uppercase with 0.07em tracking, and marks a sales claim. The uppercase and
 * the extra weight are the whole point — they read as shop signage rather than
 * as part of the interface, which is why a flag survives next to eight neutral
 * badges without being mistaken for one of them.
 *
 * Both fills carry a WHITE label, as the guide draws them. They read the
 * agency theme's roles (see merch-flag.module.css for which).
 *
 * THE PHONE DRAWS IT QUIETER, and that is the mobile guide's own call, not a
 * softening of ours. Its `.flag` is 22px, sentence case, no tracking — density
 * on a 358px rail, where uppercase at that width reads as a shout competing
 * with the number the card exists to show. The fill itself (product decision,
 * 2026-10-30) is solid at every width now, same as desktop.
 */
const TONES = {
  /** "Best seller", "Loved by families" — a claim about demand. */
  seller: styles.seller,
  /** "$200 off" — a claim about money saved, not a state. Shares the theme's
   *  success colour role for its fill (merch-flag.module.css), but the claim
   *  itself can sit side by side with an actual status badge in the booking
   *  rail — the role is borrowed for its green, not for its meaning. */
  discount: styles.discount,
} as const;

export interface MerchFlagProps {
  tone: keyof typeof TONES;
  /** Optional leading icon. Rendered at 13px to the guide's `.flag svg`. */
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function MerchFlag({ tone, icon, children, className }: MerchFlagProps) {
  return (
    <span className={cx(styles.flag, TONES[tone], className)}>
      {icon}
      {children}
    </span>
  );
}
